import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Toast, Dialog, Skeleton, List, Button, Field, Form, Empty, Tag, Selector } from 'react-vant';
import { ArrowLeft, BullhornO, DeleteO, Edit } from '@react-vant/icons';
import { getNotifications, createNotification, deleteNotification, getSessionDetail, getSessions } from '../../../api/club';
import request from '../../../api/request';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

const ClubBroadcast = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { userInfo } = useSelector(state => state.user);
  const [form] = Form.useForm();
  const [notifications, setNotifications] = useState([]);
  const [session, setSession] = useState(null);
  const [mySessions, setMySessions] = useState([]); // 老师负责的社团列表
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 权限计算：管理员，或者该社团的指导教师
  const isGlobalNotice = !sessionId || sessionId === '0';
  const isAdmin = userInfo?.role === 'admin';
  const isInstructor = session && session.club?.instructor_id === userInfo?.id;
  // 如果是全局页面，只要有负责的社团也可以发布
  const canAdd = isAdmin || (!isGlobalNotice && isInstructor) || (isGlobalNotice && mySessions.length > 0);

  useEffect(() => {
    fetchData();
  }, [sessionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [notifsRes, sessionRes] = await Promise.all([
        getNotifications(sessionId || 0),
        (sessionId && sessionId !== '0') ? getSessionDetail(sessionId) : Promise.resolve(null)
      ]);
      setNotifications(notifsRes || []);
      setSession(sessionRes);

      // 如果是全局通知页面且是教师，获取其负责的社团列表以供发布时选择
      if (isGlobalNotice && userInfo?.role === 'teacher' && !isAdmin) {
        const sessionParams = { page: 1, page_size: 100 };
        try {
          const yearRes = await request.get('/academic-year/current/');
          const currentYear = yearRes.data?.current_academic_year || yearRes.current_academic_year;
          if (currentYear) sessionParams.academic_year = currentYear;
        } catch (e) {
          console.error('获取学年失败:', e);
        }
        const sessionsRes = await getSessions(sessionParams);
        setMySessions(sessionsRes.items || []);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (values) => {
    try {
      setSubmitting(true);
      
      // 确定最终的 sessionId
      let targetSessionId = sessionId ? parseInt(sessionId) : null;
      if (isGlobalNotice && !isAdmin) {
          // 老师在全局页面发布，必须选一个社团
          targetSessionId = values.target_session_id;
          if (!targetSessionId) {
              Toast.info('请选择发布范围');
              return;
          }
      }

      const postData = {
        title: (values.title || '').trim(),
        content: (values.content || '').trim(),
        msg_type: 'announcement',
        session_id: targetSessionId && targetSessionId !== 0 ? targetSessionId : null
      };

      const res = await createNotification(postData);
      console.log('提交结果:', res);
      
      Toast.success('发布成功');
      setShowAdd(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      console.error('发布广播异常详情:', error);
      Toast.fail('发布失败: ' + (error.response?.data?.detail || error.message || '权限不足'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    Dialog.confirm({
      title: '删除确认',
      message: '确定要删除这条通知吗？',
      onConfirm: async () => {
        try {
          await deleteNotification(id);
          Toast.success('已删除');
          fetchData();
        } catch (error) {
          Toast.fail('删除失败');
        }
      }
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '32px' }}>
      <NavBar 
        title={<span style={{ fontWeight: 800 }}>社团广播</span>} 
        leftText={<ArrowLeft size={20} />} 
        onClickLeft={() => navigate(-1)}
        fixed
        placeholder
      />

      <div style={{ padding: '16px' }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', 
          borderRadius: '16px', 
          padding: '20px', 
          color: '#fff',
          marginBottom: '20px',
          boxShadow: '0 8px 20px rgba(5, 107, 79, 0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <BullhornO size={24} />
            <span style={{ fontSize: '18px', fontWeight: 800 }}>
              {session ? session.club_name : '全校公告'}
            </span>
          </div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>
            {canAdd ? '您可以发布新的通知提醒学生。' : '您当前仅可查看历史通知记录。'}
          </div>
        </div>

        {canAdd && !showAdd && (
          <Button 
            type="primary" 
            block 
            icon={<Edit />} 
            onClick={() => setShowAdd(true)}
            style={{ borderRadius: '12px', marginBottom: '20px' }}
          >
            发布新通知
          </Button>
        )}

        {showAdd && (
          <div style={{ background: '#fff', borderRadius: '16px', padding: '16px', marginBottom: '20px' }}>
            <Form form={form} onFinish={handlePost}>
              {isGlobalNotice && !isAdmin && (
                <Form.Item
                  name="target_session_id"
                  label="发布范围"
                  rules={[{ required: true, message: '请选择发布的社团' }]}
                >
                  <Selector
                    options={mySessions.map(s => ({ label: s.club_name, value: s.id }))}
                    style={{ '--rv-selector-border-radius': '8px' }}
                  />
                </Form.Item>
              )}
              <Form.Item
                name="title"
                label="标题"
                rules={[{ required: true, message: '标题不能为空' }]}
              >
                <Field placeholder="请输入通知标题" />
              </Form.Item>
              <Form.Item
                name="content"
                label="内容"
                rules={[{ required: true, message: '内容不能为空' }]}
              >
                <Field
                  type="textarea"
                  rows={3}
                  placeholder="请输入通知内容..."
                />
              </Form.Item>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '16px' }}>
                <Button type="button" block onClick={() => setShowAdd(false)}>取消</Button>
                <Button type="primary" block nativeType="submit" loading={submitting}>确认发布</Button>
              </div>
            </Form>
          </div>
        )}

        <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          历史通知
        </div>

        {loading ? (
          <Skeleton title row={5} />
        ) : notifications.length === 0 ? (
          <Empty description="暂无历史通知" />
        ) : (
          notifications.map(item => (
            <div 
              key={item.id}
              style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                position: 'relative'
              }}
            >
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                {item.title}
              </div>
              <div style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', marginBottom: '8px' }}>
                {item.content}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {dayjs(item.created_at).format('YYYY-MM-DD HH:mm')}
                </div>
                {(isAdmin || userInfo?.id === item.sender_id) && (
                  <Button 
                    size="mini" 
                    plain 
                    type="danger" 
                    icon={<DeleteO />} 
                    onClick={() => handleDelete(item.id)}
                    style={{ border: 'none' }}
                  >
                    删除
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ClubBroadcast;
